import React from 'react';
import { Circle, Square, Volume2, Link2, Radio, Users } from 'lucide-react';

/**
 * Center Console — the strip that lives between Deck A and Deck B and turns two
 * DeckPanels into one mixer. Holds: A/B live BPM readouts, SYNC (matches B's
 * tempo to A's playing BPM), MASTER volume, vertical CROSSFADER, and the
 * REC / GO LIVE calls-to-action.
 *
 * All state is owned by the parent Decks page — this component is purely
 * presentational. Every testid used by tests is preserved.
 */
export const CenterConsole = ({
  deckA, deckB,
  tempoA, setTempoA, tempoB, setTempoB,
  masterVol, setMasterVol,
  crossfade, setCrossfade,
  recording, savingMix, startRecording, stopRecordingAndPost,
  liveActive, liveViewers, onGoLiveClick,
  liveButtonRightSlot, // optional: parent renders end-stream + copy-link buttons here when live
}) => {
  const bpmA = deckA.trackBpm ? (deckA.trackBpm * tempoA) : 0;
  const bpmB = deckB.trackBpm ? (deckB.trackBpm * tempoB) : 0;
  const canSync = !!deckA.trackBpm && !!deckB.trackBpm;

  const syncBtoA = () => {
    if (!canSync) return;
    // Match B's playing BPM to A's playing BPM. Clamp to the tempo range.
    const target = deckA.trackBpm * tempoA;
    const ratio = target / deckB.trackBpm;
    setTempoB(Math.min(1.5, Math.max(0.5, +ratio.toFixed(3))));
  };

  return (
    <div
      className="rounded-2xl p-3 md:p-4 flex flex-col gap-3 min-h-full"
      data-testid="center-console"
      style={{
        background: 'linear-gradient(180deg, #0d0d0d 0%, #050505 100%)',
        boxShadow: 'inset 0 1px 0 rgba(255,176,0,0.2), inset 0 -1px 0 rgba(0,0,0,0.5), 0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,176,0,0.25)',
      }}
    >
      {/* Twin BPM readout + SYNC pill */}
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-black/50 border border-[#FFB000]/20 px-2 py-2 text-center">
          <div className="text-[8px] uppercase tracking-widest font-black text-[#FFB000]/60">Deck A</div>
          <div className="font-mono font-black text-white text-lg tabular-nums leading-tight" data-testid="console-bpm-a">
            {bpmA ? bpmA.toFixed(1) : '––'}
          </div>
        </div>
        <div className="rounded-lg bg-black/50 border border-[#FFB000]/20 px-2 py-2 text-center">
          <div className="text-[8px] uppercase tracking-widest font-black text-[#FFB000]/60">Deck B</div>
          <div className="font-mono font-black text-white text-lg tabular-nums leading-tight" data-testid="console-bpm-b">
            {bpmB ? bpmB.toFixed(1) : '––'}
          </div>
        </div>
      </div>
      <button
        onClick={syncBtoA}
        disabled={!canSync}
        title={canSync ? 'Match Deck B to Deck A' : 'Both decks need a track for SYNC'}
        className="h-10 rounded-full flex items-center justify-center gap-2 font-heading font-black text-xs tracking-widest uppercase text-black disabled:opacity-40 disabled:cursor-not-allowed transition-transform active:scale-[0.98]"
        data-testid="console-sync"
        style={{
          background: canSync ? 'linear-gradient(180deg, #FFD066 0%, #FFB000 55%, #C68600 100%)' : 'linear-gradient(180deg, #333 0%, #1a1a1a 100%)',
          boxShadow: canSync ? '0 4px 12px rgba(255,176,0,0.35), inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -2px 0 rgba(0,0,0,0.2)' : 'inset 0 1px 0 rgba(255,255,255,0.05)',
        }}
      >
        <Link2 size={12} /> Sync B → A
      </button>

      {/* Vertical crossfader — visual */}
      <div className="rounded-lg bg-black/50 border border-[#FFB000]/15 p-3 flex flex-col items-center gap-2 flex-1 min-h-[140px]">
        <div className="text-[8px] uppercase tracking-widest font-black text-[#FFB000]/60">Crossfade</div>
        <div className="w-full flex-1 flex items-center justify-center relative min-h-[80px]">
          {/* Track */}
          <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-1.5 rounded-full bg-black shadow-[inset_0_1px_2px_rgba(0,0,0,0.9)]" />
          {/* Fill from center */}
          <div
            className="absolute left-1/2 -translate-x-1/2 w-1.5 rounded-full bg-[#FFB000]/60"
            style={crossfade < 0.5
              ? { top: `${crossfade * 100}%`, bottom: '50%' }
              : { top: '50%', bottom: `${(1 - crossfade) * 100}%` }}
          />
          {/* Slider */}
          <input
            type="range" min="0" max="1" step="0.01" value={crossfade}
            onChange={e => setCrossfade(parseFloat(e.target.value))}
            className="absolute inset-0 w-full h-full appearance-none opacity-0 cursor-ns-resize"
            data-testid="crossfader"
            aria-label="Crossfader"
            style={{ writingMode: 'vertical-lr' }}
          />
          {/* Thumb */}
          <div
            className="absolute left-1/2 -translate-x-1/2 w-6 h-6 rounded-full pointer-events-none"
            style={{
              top: `calc(${crossfade * 100}% - 12px)`,
              background: 'radial-gradient(circle at 35% 30%, #FFF3C4, #FFD066 30%, #FFB000 65%, #7a4d00)',
              boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.4), inset 0 -2px 3px rgba(0,0,0,0.5), 0 0 12px rgba(255,176,0,0.5)',
              transition: 'top 80ms linear',
            }}
          />
        </div>
        <div className="flex items-center justify-between w-full text-[9px] uppercase tracking-widest font-black">
          <span className={crossfade < 0.5 ? 'text-[#FFB000]' : 'text-[#A1A1AA]/60'}>A</span>
          <span className={crossfade > 0.5 ? 'text-[#FFB000]' : 'text-[#A1A1AA]/60'}>B</span>
        </div>
      </div>

      {/* Master volume */}
      <div className="rounded-lg bg-black/50 border border-[#FFB000]/15 p-2 flex items-center gap-2">
        <Volume2 size={14} className="text-[#FFB000] flex-shrink-0" />
        <input
          type="range" min="0" max="1" step="0.01" value={masterVol}
          onChange={e => setMasterVol(parseFloat(e.target.value))}
          className="flex-1 accent-[#FFB000] cursor-ew-resize"
          data-testid="master-volume"
          aria-label="Master volume"
        />
        <span className="text-[9px] uppercase tracking-widest text-[#FFB000] font-mono font-bold w-8 text-right tabular-nums">
          {Math.round(masterVol * 100)}
        </span>
      </div>

      {/* REC + LIVE row */}
      <div className="flex flex-col gap-2">
        {!recording ? (
          <button
            onClick={startRecording}
            disabled={savingMix}
            className="h-10 rounded-full flex items-center justify-center gap-2 bg-[#EF4444] hover:bg-[#dc3838] text-white font-heading font-black text-xs uppercase tracking-widest disabled:opacity-50 transition-colors"
            data-testid="record-mix-btn"
          >
            <Circle size={12} fill="white" /> REC
          </button>
        ) : (
          <button
            onClick={stopRecordingAndPost}
            className="h-10 rounded-full flex items-center justify-center gap-2 bg-[#FFB000] hover:bg-[#E69E00] text-black font-heading font-black text-xs uppercase tracking-widest animate-pulse-glow"
            data-testid="stop-record-btn"
          >
            <Square size={12} fill="black" /> STOP · POST
          </button>
        )}

        {!liveActive ? (
          <button
            onClick={onGoLiveClick}
            className="h-10 rounded-full flex items-center justify-center gap-2 bg-black border border-[#FFB000] text-[#FFB000] hover:bg-[#FFB000] hover:text-black font-heading font-black text-xs uppercase tracking-widest transition-colors"
            data-testid="go-live-btn"
          >
            <Radio size={12} /> Go Live
          </button>
        ) : (
          <div className="flex flex-col gap-2" data-testid="live-toolbar">
            <div className="rounded-full bg-[#EF4444]/15 border border-[#EF4444]/40 text-[#EF4444] font-heading font-black text-[10px] uppercase tracking-widest h-10 flex items-center justify-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] animate-pulse" /> ON AIR
              <span className="flex items-center gap-1 ml-2 text-[#FFB000]"><Users size={10} /> {liveViewers}</span>
            </div>
            {liveButtonRightSlot}
          </div>
        )}
      </div>
    </div>
  );
};
